import { useEffect, useState, useSyncExternalStore } from 'react';
import { LiveSocket } from './socket.ts';

const noopSub = () => () => {};

/**
 * Open a live socket for `url` for as long as the component is mounted
 * (created in an effect, so StrictMode's double mount cannot orphan it).
 */
export function useSocket<V>(url: string | null, check?: () => Promise<boolean>): { sock: LiveSocket<V> | null; view: V | null; status: LiveSocket<V>['status'] } {
  const [sock, setSock] = useState<LiveSocket<V> | null>(null);
  useEffect(() => {
    if (!url) return;
    const s = new LiveSocket<V>(url, check);
    setSock(s);
    return () => {
      s.close();
      setSock((cur) => (cur === s ? null : cur));
    };
    // check is a closure over the same token as url
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);
  const view = useSyncExternalStore(sock ? sock.subscribe : noopSub, () => (sock ? sock.view : null));
  const status = useSyncExternalStore(sock ? sock.subscribe : noopSub, () => (sock ? sock.status : 'connecting'));
  return { sock, view, status };
}

/** Re-render every `ms` (for countdowns). */
export function useNow(ms = 250): number {
  const [, set] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => set((x) => x + 1), ms);
    return () => window.clearInterval(id);
  }, [ms]);
  return Date.now();
}

/** Keep the screen awake while playing (feature-detected). */
export function useWakeLock(on: boolean): void {
  useEffect(() => {
    if (!on || !('wakeLock' in navigator)) return;
    let lock: WakeLockSentinel | null = null;
    let cancelled = false;
    const get = async () => {
      try {
        lock = await navigator.wakeLock.request('screen');
      } catch {
        /* denied or unsupported: fine */
      }
    };
    void get();
    const onVis = () => {
      if (document.visibilityState === 'visible' && !cancelled) void get();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVis);
      void lock?.release().catch(() => {});
    };
  }, [on]);
}
